import React, { useState, useEffect, useCallback } from 'react';
import { employeeAccountsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/ConfirmModal';
import {
  FiDollarSign, FiCreditCard, FiFileText, FiPlus, FiTrash2,
  FiCheck, FiX, FiTrendingDown
} from 'react-icons/fi';

/* ── helpers ──────────────────────────────────────────── */
const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_CFG = {
  pending:  { bg: '#fef3c7', text: '#92400e', label: 'Pending' },
  approved: { bg: '#dcfce7', text: '#14532d', label: 'Approved' },
  active:   { bg: '#dbeafe', text: '#1e3a8a', label: 'Active' },
  rejected: { bg: '#fee2e2', text: '#991b1b', label: 'Rejected' },
  settled:  { bg: '#f3f4f6', text: '#374151', label: 'Settled' },
  paid:     { bg: '#dcfce7', text: '#14532d', label: 'Paid' },
};

const REINB_CATS = [
  'travel','food','accommodation','communication','medical',
  'office_supplies','training','client_entertainment','other'
];
const CAT_LABELS = {
  travel:'Travel', food:'Food & Dining', accommodation:'Accommodation',
  communication:'Communication', medical:'Medical', office_supplies:'Office Supplies',
  training:'Training', client_entertainment:'Client Entertainment', other:'Other',
};

const INP = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #e2e8f0',
  borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  background: '#fafbfc', fontFamily: 'inherit', transition: 'border .2s',
};
const SEL = { ...INP, cursor: 'pointer' };

/* ── StatusBadge ──────────────────────────────────────── */
function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: cfg.bg, color: cfg.text,
    }}>{cfg.label}</span>
  );
}

/* ── Modal wrapper ────────────────────────────────────── */
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 32, width: 520,
        maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8' }}>
            <FiX />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   ADVANCES TAB
════════════════════════════════════════════════════════ */
function AdvancesTab({ isAdmin, userId }) {
  const confirm = useConfirm();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ amount: '', reason: '', deductionPerMonth: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: '' });
  const [payModal, setPayModal] = useState({ open: false, id: null, amount: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await employeeAccountsAPI.getAdvances();
      setRecords(r.data.data || []);
    } catch { toast.error('Failed to load advances'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await employeeAccountsAPI.createAdvance(form);
      toast.success('Advance request submitted');
      setModal(false);
      setForm({ amount: '', reason: '', deductionPerMonth: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await employeeAccountsAPI.updateAdvanceStatus(id, { status, ...extra });
      toast.success(`Advance ${status}`);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    const ok = await confirm('Delete this advance request?', { title: 'Delete Advance?', variant: 'danger' });
    if (!ok) return;
    try { await employeeAccountsAPI.deleteAdvance(id); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const recordDeduction = async () => {
    try {
      await employeeAccountsAPI.recordDeduction(payModal.id, { amount: payModal.amount });
      toast.success('Deduction recorded');
      setPayModal({ open: false, id: null, amount: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <p style={{ color: '#64748b', margin: 0 }}>{records.length} advance request{records.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setModal(true)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px',
          background: 'var(--pm-gradient)', color: '#fff',
          border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14,
        }}><FiPlus /> Request Advance</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Employee', 'Amount', 'Reason', 'Deduction/Mo', 'Recovered', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No advances found</td></tr>
              ) : records.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 14px' }}>{r.employee?.name || '—'}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600 }}>{fmt(r.amount)}</td>
                  <td style={{ padding: '12px 14px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
                  <td style={{ padding: '12px 14px' }}>{fmt(r.deductionPerMonth)}</td>
                  <td style={{ padding: '12px 14px' }}>{fmt(r.deductedSoFar)} / {fmt(r.amount)}</td>
                  <td style={{ padding: '12px 14px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding: '12px 14px', color: '#64748b' }}>{new Date(r.createdAt).toLocaleDateString('en-IN')}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {isAdmin && r.status === 'pending' && (
                        <>
                          <button onClick={() => updateStatus(r.id, 'approved')} title="Approve"
                            style={{ padding: '5px 8px', background: '#dcfce7', color: '#14532d', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                            <FiCheck size={14} />
                          </button>
                          <button onClick={() => setRejectModal({ open: true, id: r.id, reason: '' })} title="Reject"
                            style={{ padding: '5px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                            <FiX size={14} />
                          </button>
                        </>
                      )}
                      {isAdmin && r.status === 'approved' && (
                        <button onClick={() => setPayModal({ open: true, id: r.id, amount: r.deductionPerMonth || '' })} title="Record Deduction"
                          style={{ padding: '5px 10px', background: '#dbeafe', color: '#1e3a8a', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          <FiTrendingDown size={13} />
                        </button>
                      )}
                      {r.status === 'pending' && (
                        <button onClick={() => del(r.id)} title="Delete"
                          style={{ padding: '5px 8px', background: '#f1f5f9', color: '#ef4444', border: 'none', borderRadius: 7, cursor: 'pointer' }}>
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {modal && (
        <Modal title="Request Salary Advance" onClose={() => setModal(false)}>
          <form onSubmit={submit}>
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Amount *</label>
                <input type="number" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                  placeholder="₹ 0.00" style={INP} />
              </div>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Reason *</label>
                <textarea required value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                  placeholder="Reason for advance..." rows={3} style={{ ...INP, resize: 'vertical' }} />
              </div>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Preferred Monthly Deduction</label>
                <input type="number" value={form.deductionPerMonth} onChange={e => setForm({ ...form, deductionPerMonth: e.target.value })}
                  placeholder="₹ 0.00" style={INP} />
              </div>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  rows={2} style={{ ...INP, resize: 'vertical' }} />
              </div>
              <button type="submit" disabled={saving} style={{
                padding: '11px', background: 'var(--pm-gradient)',
                color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15,
              }}>{saving ? 'Submitting…' : 'Submit Request'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reject Modal */}
      {rejectModal.open && (
        <Modal title="Reject Advance" onClose={() => setRejectModal({ open: false, id: null, reason: '' })}>
          <textarea value={rejectModal.reason} onChange={e => setRejectModal({ ...rejectModal, reason: e.target.value })}
            placeholder="Reason for rejection..." rows={4} style={{ ...INP, marginBottom: 16, resize: 'vertical' }} />
          <button onClick={() => { updateStatus(rejectModal.id, 'rejected', { rejectionReason: rejectModal.reason }); setRejectModal({ open: false, id: null, reason: '' }); }}
            style={{ width: '100%', padding: 11, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
            Reject Advance
          </button>
        </Modal>
      )}

      {/* Payment Modal */}
      {payModal.open && (
        <Modal title="Record EMI Deduction" onClose={() => setPayModal({ open: false, id: null, amount: '' })}>
          <input type="number" value={payModal.amount} onChange={e => setPayModal({ ...payModal, amount: e.target.value })}
            placeholder="Amount deducted this month" style={{ ...INP, marginBottom: 16 }} />
          <button onClick={recordDeduction}
            style={{ width: '100%', padding: 11, background: 'var(--pm-primary)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
            Record Deduction
          </button>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   LOANS TAB
════════════════════════════════════════════════════════ */
function LoansTab({ isAdmin }) {
  const confirm = useConfirm();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ amount: '', purpose: '', monthlyInstallment: '', interestRate: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: '' });
  const [approveModal, setApproveModal] = useState({ open: false, id: null, installment: '', startDate: today() });
  const [payModal, setPayModal] = useState({ open: false, id: null, amount: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await employeeAccountsAPI.getLoans();
      setRecords(r.data.data || []);
    } catch { toast.error('Failed to load loans'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await employeeAccountsAPI.createLoan(form);
      toast.success('Loan request submitted');
      setModal(false);
      setForm({ amount: '', purpose: '', monthlyInstallment: '', interestRate: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const reject = async () => {
    try {
      await employeeAccountsAPI.updateLoanStatus(rejectModal.id, { status: 'rejected', rejectionReason: rejectModal.reason });
      toast.success('Loan rejected'); setRejectModal({ open: false, id: null, reason: '' }); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const approve = async () => {
    try {
      await employeeAccountsAPI.updateLoanStatus(approveModal.id, {
        status: 'active',
        monthlyInstallment: approveModal.installment,
        startDate: approveModal.startDate,
      });
      toast.success('Loan approved'); setApproveModal({ open: false, id: null, installment: '', startDate: today() }); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const recordPayment = async () => {
    try {
      await employeeAccountsAPI.recordPayment(payModal.id, { amount: payModal.amount });
      toast.success('Payment recorded');
      setPayModal({ open: false, id: null, amount: '' }); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    const ok = await confirm('Delete this loan request?', { title: 'Delete Loan?', variant: 'danger' });
    if (!ok) return;
    try { await employeeAccountsAPI.deleteLoan(id); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <p style={{ color: '#64748b', margin: 0 }}>{records.length} loan request{records.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setModal(true)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px',
          background: 'var(--pm-gradient)', color: '#fff',
          border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14,
        }}><FiPlus /> Request Loan</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Employee', 'Loan Amount', 'Purpose', 'Monthly EMI', 'Paid', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No loans found</td></tr>
              ) : records.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 14px' }}>{r.employee?.name || '—'}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600 }}>{fmt(r.amount)}</td>
                  <td style={{ padding: '12px 14px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.purpose}</td>
                  <td style={{ padding: '12px 14px' }}>{fmt(r.monthlyInstallment)}</td>
                  <td style={{ padding: '12px 14px' }}>{fmt(r.totalPaid)} / {fmt(r.amount)}</td>
                  <td style={{ padding: '12px 14px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding: '12px 14px', color: '#64748b' }}>{new Date(r.createdAt).toLocaleDateString('en-IN')}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {isAdmin && r.status === 'pending' && (
                        <>
                          <button onClick={() => setApproveModal({ open: true, id: r.id, installment: r.monthlyInstallment || '', startDate: today() })} title="Approve"
                            style={{ padding: '5px 8px', background: '#dcfce7', color: '#14532d', border: 'none', borderRadius: 7, cursor: 'pointer' }}><FiCheck size={14} /></button>
                          <button onClick={() => setRejectModal({ open: true, id: r.id, reason: '' })} title="Reject"
                            style={{ padding: '5px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 7, cursor: 'pointer' }}><FiX size={14} /></button>
                        </>
                      )}
                      {isAdmin && r.status === 'active' && (
                        <button onClick={() => setPayModal({ open: true, id: r.id, amount: r.monthlyInstallment || '' })} title="Record Installment"
                          style={{ padding: '5px 10px', background: '#dbeafe', color: '#1e3a8a', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          <FiTrendingDown size={13} />
                        </button>
                      )}
                      {r.status === 'pending' && (
                        <button onClick={() => del(r.id)} title="Delete"
                          style={{ padding: '5px 8px', background: '#f1f5f9', color: '#ef4444', border: 'none', borderRadius: 7, cursor: 'pointer' }}><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title="Request Loan" onClose={() => setModal(false)}>
          <form onSubmit={submit}>
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Loan Amount *</label>
                <input type="number" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={INP} placeholder="₹ 0.00" />
              </div>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Purpose *</label>
                <textarea required value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} rows={3} style={{ ...INP, resize: 'vertical' }} placeholder="Purpose of loan..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Preferred EMI / Month</label>
                  <input type="number" value={form.monthlyInstallment} onChange={e => setForm({ ...form, monthlyInstallment: e.target.value })} style={INP} placeholder="₹ 0.00" />
                </div>
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Interest Rate (%)</label>
                  <input type="number" value={form.interestRate} onChange={e => setForm({ ...form, interestRate: e.target.value })} style={INP} placeholder="0" />
                </div>
              </div>
              <button type="submit" disabled={saving} style={{ padding: '11px', background: 'var(--pm-gradient)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
                {saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {approveModal.open && (
        <Modal title="Approve Loan" onClose={() => setApproveModal({ open: false, id: null, installment: '', startDate: today() })}>
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Monthly Installment *</label>
              <input type="number" value={approveModal.installment} onChange={e => setApproveModal({ ...approveModal, installment: e.target.value })} style={INP} placeholder="₹ 0.00" />
            </div>
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Start Date</label>
              <input type="date" value={approveModal.startDate} onChange={e => setApproveModal({ ...approveModal, startDate: e.target.value })} style={INP} />
            </div>
            <button onClick={approve} style={{ padding: '11px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
              Approve Loan
            </button>
          </div>
        </Modal>
      )}

      {rejectModal.open && (
        <Modal title="Reject Loan" onClose={() => setRejectModal({ open: false, id: null, reason: '' })}>
          <textarea value={rejectModal.reason} onChange={e => setRejectModal({ ...rejectModal, reason: e.target.value })}
            placeholder="Reason..." rows={4} style={{ ...INP, marginBottom: 16, resize: 'vertical' }} />
          <button onClick={reject} style={{ width: '100%', padding: 11, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Reject Loan</button>
        </Modal>
      )}

      {payModal.open && (
        <Modal title="Record Installment Payment" onClose={() => setPayModal({ open: false, id: null, amount: '' })}>
          <input type="number" value={payModal.amount} onChange={e => setPayModal({ ...payModal, amount: e.target.value })}
            placeholder="Amount paid this month" style={{ ...INP, marginBottom: 16 }} />
          <button onClick={recordPayment} style={{ width: '100%', padding: 11, background: 'var(--pm-primary)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Record Payment</button>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   REIMBURSEMENTS TAB
════════════════════════════════════════════════════════ */
function ReimbursementsTab({ isAdmin }) {
  const confirm = useConfirm();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ category: 'other', description: '', amount: '', expenseDate: today(), receiptUrl: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [rejectModal, setRejectModal] = useState({ open: false, id: null, reason: '' });
  const [payModal, setPayModal] = useState({ open: false, id: null, method: 'bank' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await employeeAccountsAPI.getReimbursements();
      setRecords(r.data.data || []);
    } catch { toast.error('Failed to load reimbursements'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await employeeAccountsAPI.createReimbursement(form);
      toast.success('Reimbursement request submitted');
      setModal(false);
      setForm({ category: 'other', description: '', amount: '', expenseDate: today(), receiptUrl: '', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id, status, extra = {}) => {
    try {
      await employeeAccountsAPI.updateReimbStatus(id, { status, ...extra });
      toast.success(`Reimbursement ${status}`);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const del = async (id) => {
    const ok = await confirm('Delete this request?', { title: 'Delete Request?', variant: 'danger' });
    if (!ok) return;
    try { await employeeAccountsAPI.deleteReimbursement(id); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <p style={{ color: '#64748b', margin: 0 }}>{records.length} reimbursement{records.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setModal(true)} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px',
          background: 'var(--pm-gradient)', color: '#fff',
          border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14,
        }}><FiPlus /> New Claim</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Employee', 'Category', 'Description', 'Amount', 'Expense Date', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>No reimbursements found</td></tr>
              ) : records.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 14px' }}>{r.employee?.name || '—'}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>{CAT_LABELS[r.category] || r.category}</span>
                  </td>
                  <td style={{ padding: '12px 14px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</td>
                  <td style={{ padding: '12px 14px', fontWeight: 600 }}>{fmt(r.amount)}</td>
                  <td style={{ padding: '12px 14px', color: '#64748b' }}>{r.expenseDate}</td>
                  <td style={{ padding: '12px 14px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {isAdmin && r.status === 'pending' && (
                        <>
                          <button onClick={() => updateStatus(r.id, 'approved')} title="Approve"
                            style={{ padding: '5px 8px', background: '#dcfce7', color: '#14532d', border: 'none', borderRadius: 7, cursor: 'pointer' }}><FiCheck size={14} /></button>
                          <button onClick={() => setRejectModal({ open: true, id: r.id, reason: '' })} title="Reject"
                            style={{ padding: '5px 8px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: 7, cursor: 'pointer' }}><FiX size={14} /></button>
                        </>
                      )}
                      {isAdmin && r.status === 'approved' && (
                        <button onClick={() => setPayModal({ open: true, id: r.id, method: 'bank' })} title="Mark Paid"
                          style={{ padding: '5px 10px', background: '#dcfce7', color: '#14532d', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          Mark Paid
                        </button>
                      )}
                      {r.receiptUrl && (
                        <a href={r.receiptUrl} target="_blank" rel="noopener noreferrer"
                          style={{ padding: '5px 8px', background: '#f1f5f9', color: 'var(--pm-primary)', border: 'none', borderRadius: 7, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                          <FiFileText size={14} />
                        </a>
                      )}
                      {r.status === 'pending' && (
                        <button onClick={() => del(r.id)} title="Delete"
                          style={{ padding: '5px 8px', background: '#f1f5f9', color: '#ef4444', border: 'none', borderRadius: 7, cursor: 'pointer' }}><FiTrash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title="Expense Reimbursement Claim" onClose={() => setModal(false)}>
          <form onSubmit={submit}>
            <div style={{ display: 'grid', gap: 16 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Category *</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={SEL}>
                  {REINB_CATS.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Description *</label>
                <textarea required value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3} style={{ ...INP, resize: 'vertical' }} placeholder="Describe the expense..." />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Amount *</label>
                  <input type="number" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={INP} placeholder="₹ 0.00" />
                </div>
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Expense Date *</label>
                  <input type="date" required value={form.expenseDate} onChange={e => setForm({ ...form, expenseDate: e.target.value })} style={INP} />
                </div>
              </div>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Receipt URL / Link</label>
                <input type="url" value={form.receiptUrl} onChange={e => setForm({ ...form, receiptUrl: e.target.value })} style={INP} placeholder="https://..." />
              </div>
              <button type="submit" disabled={saving} style={{ padding: '11px', background: 'var(--pm-gradient)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>
                {saving ? 'Submitting…' : 'Submit Claim'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {rejectModal.open && (
        <Modal title="Reject Reimbursement" onClose={() => setRejectModal({ open: false, id: null, reason: '' })}>
          <textarea value={rejectModal.reason} onChange={e => setRejectModal({ ...rejectModal, reason: e.target.value })}
            rows={4} placeholder="Reason..." style={{ ...INP, marginBottom: 16, resize: 'vertical' }} />
          <button onClick={() => { updateStatus(rejectModal.id, 'rejected', { rejectionReason: rejectModal.reason }); setRejectModal({ open: false, id: null, reason: '' }); }}
            style={{ width: '100%', padding: 11, background: '#ef4444', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Reject</button>
        </Modal>
      )}

      {payModal.open && (
        <Modal title="Mark as Paid" onClose={() => setPayModal({ open: false, id: null, method: 'bank' })}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, fontSize: 13, color: '#475569', display: 'block', marginBottom: 6 }}>Payment Method</label>
            <select value={payModal.method} onChange={e => setPayModal({ ...payModal, method: e.target.value })} style={SEL}>
              <option value="bank">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
            </select>
          </div>
          <button onClick={() => { updateStatus(payModal.id, 'paid', { paymentMethod: payModal.method }); setPayModal({ open: false, id: null, method: 'bank' }); }}
            style={{ width: '100%', padding: 11, background: '#22c55e', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>Mark as Paid</button>
        </Modal>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════ */
const TABS = [
  { key: 'advances',       label: 'Advances',        icon: <FiDollarSign /> },
  { key: 'loans',          label: 'Loans',           icon: <FiCreditCard /> },
  { key: 'reimbursements', label: 'Reimbursements',  icon: <FiFileText /> },
];

export default function EmployeeAccounts() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('advances');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      employeeAccountsAPI.getStats()
        .then(r => setStats(r.data.data))
        .catch(() => {});
    }
  }, [isAdmin]);

  const STAT_CARDS = stats ? [
    { label: 'Pending Advances',       val: stats.pendingAdv,   color: '#f59e0b' },
    { label: 'Pending Loans',          val: stats.pendingLoans, color: 'var(--pm-primary)' },
    { label: 'Pending Reimbursements', val: stats.pendingReimb, color: '#10b981' },
    { label: 'Total Pending',          val: stats.totalPending, color: '#ef4444' },
  ] : [];

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>Employee Accounts</h1>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 15 }}>
          Manage salary advances, loans and expense reimbursements
        </p>
      </div>

      {/* Stats (admin only) */}
      {isAdmin && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 16, marginBottom: 28 }}>
          {STAT_CARDS.map(s => (
            <div key={s.label} style={{
              background: '#fff', borderRadius: 14, padding: '20px 22px',
              boxShadow: '0 1px 6px rgba(0,0,0,0.07)', borderTop: `3px solid ${s.color}`,
            }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        background: '#fff', borderRadius: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflow: 'hidden',
      }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', padding: '0 24px' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '16px 20px',
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              color: activeTab === t.key ? 'var(--pm-primary)' : '#64748b',
              borderBottom: activeTab === t.key ? '2px solid #6366f1' : '2px solid transparent',
              marginBottom: -1, transition: 'all .15s',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: 24 }}>
          {activeTab === 'advances' && <AdvancesTab isAdmin={isAdmin} userId={user?.id} />}
          {activeTab === 'loans' && <LoansTab isAdmin={isAdmin} />}
          {activeTab === 'reimbursements' && <ReimbursementsTab isAdmin={isAdmin} />}
        </div>
      </div>
    </div>
  );
}
